import { getConsent, onConsentChange } from './consent'
import { pushTicker, tickerEnabled } from './ticker'
import { setVitalsReporter } from './web-vitals'

/**
 * Analytics core (spec §5.3, Appendix A = THE CONTRACT).
 *
 * - No-op unless consent is granted (decline => zero events in every mode,
 *   including the dev logger — that IS the decline QA check).
 * - Every event carries render_mode, session_id (per-tab join key between
 *   GA4/BigQuery and PostHog recordings) and schema_version: 2.
 * - Fan-out per ROUTING; conversion/exit-adjacent events ride the beacon.
 * - With no IDs configured (localhost default): dev-logger mode — structured
 *   console lines + the on-screen ticker (?debug_analytics=1).
 */

export type EventName =
  // S — system
  | 'canvas_ready' | 'web_vitals' | 'client_error' | 'fps_sample'
  // N — navigation/spatial
  | 'zone_view' | 'zone_dwell' | 'pan_burst' | 'zoom_change' | 'hud_use'
  | 'minimap_jump' | 'overview_mode' | 'canvas_wander' | 'deep_link_open'
  | 'canvas_click' | 'page_view'
  // T — tour
  | 'tour_start' | 'tour_step' | 'tour_pause' | 'tour_resume'
  | 'tour_takeover' | 'tour_skip' | 'tour_complete'
  // D — drag
  | 'drag_start' | 'drag_end'
  // G — games
  | 'game_open' | 'game_start' | 'game_progress' | 'game_complete'
  | 'game_abandon' | 'game_replay'
  // E — eggs
  | 'easter_egg_view' | 'easter_egg_found'
  // C — components/conversions
  | 'component_interact' | 'cta_click' | 'github_click' | 'docs_get_started'
  | 'waitlist_submit' | 'community_join'
  // M/X — linear + docs
  | 'scroll_depth' | 'docs_search' | 'code_copy' | 'docs_feedback'
  // proposed in Phase D FLAGS (approved direction: PH-only curiosity signal)
  | 'login_attempt'

export type SinkRoute = 'ga' | 'ph' | 'both'
export type Params = Record<string, string | number | boolean | undefined>
export type RenderMode = 'canvas' | 'linear'

/** Appendix A Route column, verbatim. */
export const ROUTING: Record<EventName, SinkRoute> = {
  canvas_ready: 'both', web_vitals: 'both', client_error: 'ph', fps_sample: 'ph',
  zone_view: 'both', zone_dwell: 'both', pan_burst: 'ph', zoom_change: 'ph',
  hud_use: 'both', minimap_jump: 'both', overview_mode: 'ph', canvas_wander: 'both',
  deep_link_open: 'both', canvas_click: 'both', page_view: 'ga',
  tour_start: 'both', tour_step: 'ph', tour_pause: 'ph', tour_resume: 'ph',
  tour_takeover: 'both', tour_skip: 'both', tour_complete: 'both',
  drag_start: 'ph', drag_end: 'both',
  game_open: 'both', game_start: 'both', game_progress: 'ph', game_complete: 'both',
  game_abandon: 'both', game_replay: 'ph',
  easter_egg_view: 'both', easter_egg_found: 'both',
  component_interact: 'both', cta_click: 'both', github_click: 'both',
  docs_get_started: 'both', waitlist_submit: 'both', community_join: 'both',
  scroll_depth: 'ga', docs_search: 'both', code_copy: 'both', docs_feedback: 'both',
  login_attempt: 'ph',
}

/** Conversion + exit-adjacent events ride navigator.sendBeacon-capable transports. */
const BEACON = new Set<EventName>([
  'zone_dwell', 'cta_click', 'github_click', 'docs_get_started', 'waitlist_submit', 'community_join',
])

export const GA4_ID: string | undefined = import.meta.env.VITE_GA4_ID || undefined
export const POSTHOG_KEY: string | undefined = import.meta.env.VITE_POSTHOG_KEY || undefined
export const POSTHOG_HOST: string = import.meta.env.VITE_POSTHOG_HOST || 'https://eu.i.posthog.com'

export function analyticsMode(): 'live' | 'dev-logger' {
  return GA4_ID || POSTHOG_KEY ? 'live' : 'dev-logger'
}

// ---------------------------------------------------------------------------
// session + stamps

export function sessionId(): string {
  try {
    let id = sessionStorage.getItem('shinro-session-id')
    if (!id) {
      id = crypto.randomUUID()
      sessionStorage.setItem('shinro-session-id', id)
    }
    return id
  } catch {
    return 'no-storage'
  }
}

let renderMode: RenderMode = 'canvas'

// ---------------------------------------------------------------------------
// sinks

type Gtag = (...args: unknown[]) => void
function gtag(): Gtag | null {
  const w = window as unknown as { gtag?: Gtag; dataLayer?: unknown[] }
  if (typeof w.gtag === 'function') return w.gtag
  if (Array.isArray(w.dataLayer)) return function (this: unknown) { w.dataLayer!.push(arguments) } as unknown as Gtag
  return null
}

interface PosthogLike {
  init: (key: string, opts: Record<string, unknown>) => void
  capture: (name: string, props?: Record<string, unknown>, opts?: Record<string, unknown>) => void
  register: (props: Record<string, unknown>) => void
  opt_out_capturing: () => void
  opt_in_capturing: () => void
}
let posthog: PosthogLike | null = null

/** Create window.gtag + dataLayer if the loader hasn't run yet (loader is grant-gated). */
function ensureGtag(): Gtag {
  const w = window as unknown as { gtag?: Gtag; dataLayer?: unknown[] }
  w.dataLayer = w.dataLayer || []
  if (typeof w.gtag !== 'function') {
    w.gtag = function (this: unknown) { w.dataLayer!.push(arguments) } as unknown as Gtag
  }
  return w.gtag
}

async function enableSinks(): Promise<void> {
  if (analyticsMode() === 'dev-logger') return
  if (GA4_ID) {
    // Consent Mode v2, entirely grant-gated: the gtag.js library itself is only
    // injected AFTER Accept, so declining (or ignoring) the banner means zero
    // requests to Google — matching the /privacy page's wording literally.
    // Queue order: js -> consent default -> consent update -> config.
    const g = ensureGtag()
    g('js', new Date())
    g('consent', 'default', {
      analytics_storage: 'denied', ad_storage: 'denied',
      ad_user_data: 'denied', ad_personalization: 'denied',
    })
    g('consent', 'update', { analytics_storage: 'granted' })
    g('config', GA4_ID, { send_page_view: false })
    if (!document.querySelector('script[src*="googletagmanager.com/gtag/js"]')) {
      const loader = document.createElement('script')
      loader.async = true
      loader.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`
      document.head.appendChild(loader)
    }
  }
  if (POSTHOG_KEY && !posthog) {
    // posthog-js loads only after grant — zero requests to the PH host before that.
    const mod = await import('posthog-js')
    posthog = mod.default as unknown as PosthogLike
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      capture_pageview: false,
      autocapture: true,
      mask_all_text: false,
      mask_all_element_attributes: false,
      // posthog-js 1.407: canvas options live under session_recording.captureCanvas
      session_recording: {
        maskAllInputs: true,
        captureCanvas: { recordCanvas: true, canvasFps: 4, canvasQuality: '0.4' },
      },
    })
    posthog.register({ session_id: sessionId(), schema_version: 2 })
    posthog.opt_in_capturing()
  }
}

/** GDPR Art. 7(3): withdrawal must actually stop collection, not just mute track(). */
function disableSinks(): void {
  if (GA4_ID) gtag()?.('consent', 'update', { analytics_storage: 'denied' })
  posthog?.opt_out_capturing()
}

// ---------------------------------------------------------------------------
// public API

export function initAnalytics(mode: RenderMode): void {
  renderMode = mode
  setVitalsReporter((m) => track('web_vitals', { metric: m.metric, value: m.value, rating: m.rating }))
  if (getConsent() === 'granted') void enableSinks()
  onConsentChange((value) => {
    if (value === 'granted') void enableSinks()
    else disableSinks()
  })
}

// S2 session props: registered once, on the first delivered event of the session.
let sessionProps: Params | null = null
let sessionPropsSent = false
export function setSessionProps(props: Params): void {
  sessionProps = props
}

function deliverSessionProps(): void {
  if (sessionPropsSent || !sessionProps) return
  sessionPropsSent = true
  if (analyticsMode() === 'dev-logger') {
    console.log('[analytics:props]', sessionProps)
    return
  }
  posthog?.register({ ...sessionProps })
  if (GA4_ID) {
    gtag()?.('set', 'user_properties', {
      consent_mode: 'granted',
      features_available: sessionProps.features_available,
    })
    gtag()?.('set', { shinro_session_id: sessionId() })
  }
}

export function track(name: EventName, params: Params = {}): void {
  if (getConsent() !== 'granted') return
  deliverSessionProps()
  const route = ROUTING[name]
  const payload: Params = {
    ...params,
    render_mode: renderMode,
    session_id: sessionId(),
    schema_version: 2,
  }

  if (analyticsMode() === 'dev-logger') {
    console.log(`[analytics:${route}] ${name}`, payload)
    if (tickerEnabled()) pushTicker(route, name, payload)
    return
  }

  if (route !== 'ph' && GA4_ID) {
    // GA4 REJECTS 'session_id' as a custom-dimension parameter ("Parameter name is
    // not allowed for this scope" — it collides with GA4's own session identifier),
    // so the PostHog<->GA4 join key travels to GA as shinro_session_id. PostHog keeps
    // the contract name. Registered as an event-scoped custom dimension in GA4.
    const { session_id, ...rest } = payload
    const gaParams: Record<string, unknown> = { ...rest, shinro_session_id: session_id }
    if (BEACON.has(name)) gaParams.transport_type = 'beacon'
    gtag()?.('event', name, gaParams)
  }
  if (route !== 'ga' && posthog) {
    posthog.capture(name, payload, BEACON.has(name) ? { transport: 'sendBeacon' } : undefined)
  }
  if (tickerEnabled()) {
    // Live mode is otherwise silent, which is why the QA matrix could only ever be
    // run against a keyless build. Under the explicit ?debug_analytics=1 flag we
    // emit the same structured line the dev logger does, so one collector reads
    // both modes and `npm run qa:matrix --base https://shinro.dev` works.
    console.log(`[analytics:${route}] ${name}`, payload)
    pushTicker(route, name, payload)
  }
}

/** Debounced wrapper around track (trailing edge). */
export function debouncedTrack(name: EventName, ms: number): (params?: Params) => void {
  let timer: ReturnType<typeof setTimeout> | null = null
  return (params: Params = {}) => {
    if (timer !== null) clearTimeout(timer)
    timer = setTimeout(() => { timer = null; track(name, params) }, ms)
  }
}

/** Fire at most once per tab session (keyed by event name + optional discriminator). */
const oncePerFired = new Set<string>()
export function oncePerSession(name: EventName, params: Params = {}, key = ''): void {
  const k = `${name}:${key}`
  if (oncePerFired.has(k)) return
  oncePerFired.add(k)
  track(name, params)
}
